import { ReactNode } from 'react';
import { TSelfEnrollmentStatus, TSessionDetails } from '@api/io/session.io';
import { ContinueButton } from '@components/EnrollmentActions/ContinueButton';
import { DisenrollButton } from '@routes/session-details/components/Enrollment/Actions/DisenrollButton';
import { EnrollButton } from '@routes/session-details/components/Enrollment/Actions/EnrollButton';
import { useConfigContext } from '@fe-core';
import { isProjectSelection } from '@fe-learner-common/api/schemas/sessionsSchema';
import { isCompletedProjectSelection } from '@utils/isCompletedProjectSelection';

type TEnrollmentAction = {
  enrollmentAction: ReactNode;
  disenrollmentAction?: ReactNode;
};

export const enrollmentActionsMap: Record<
  TSelfEnrollmentStatus,
  ({
    sessionDetails,
    baseUrl,
  }: {
    sessionDetails: TSessionDetails;
    baseUrl: string;
  }) => TEnrollmentAction
> = {
  unenrolled: ({ sessionDetails }) => ({
    enrollmentAction: sessionDetails.enrollment.enabled && (
      <EnrollButton sessionDetails={sessionDetails} />
    ),
    disenrollmentAction: null,
  }),
  active: ({ sessionDetails, baseUrl }) => {
    // The /session redirect resolves to a participation view, which aleweb refuses for project selections.
    // For those, go to the selection's own landing page (the course picker) instead.
    const isSelection = isProjectSelection(sessionDetails);
    const selectionLink = sessionDetails.selfEnrollmentParticipationUrl;

    return {
      enrollmentAction: !isCompletedProjectSelection(sessionDetails) && (
        <ContinueButton
          link={
            isSelection && selectionLink
              ? selectionLink
              : `${baseUrl}session/${sessionDetails.id}`
          }
          progress={sessionDetails.selfEnrollmentProgress}
          labelId={isSelection ? 'courses.project.tag.selectCourse' : undefined}
        />
      ),
      disenrollmentAction: (
        <DisenrollButton
          sessionId={sessionDetails.id}
          disabled={!sessionDetails.disenrollment.enabled}
        />
      ),
    };
  },
  enrolled_confirmed: ({ sessionDetails }) => ({
    enrollmentAction: null,
    disenrollmentAction: sessionDetails.enrollment.cancellable && (
      <DisenrollButton sessionId={sessionDetails.id} disabled={false} />
    ),
  }),
  unknown: () => ({
    enrollmentAction: null,
    disenrollmentAction: null,
  }),
};

type Props = {
  sessionDetails: TSessionDetails;
};

export const useEnrollmentActions = ({ sessionDetails }: Props) => {
  const { config } = useConfigContext();
  if (!sessionDetails.selfEnrollmentStatus) {
    return null;
  }

  return enrollmentActionsMap[sessionDetails.selfEnrollmentStatus]({
    sessionDetails,
    baseUrl: config.baseUrls.aleweb as string,
  });
};
